import React, { useState } from "react";
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
} from "@musa.tech/design-system";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { isDateBlocked } from "@/utils/dateBlockingUtils";
import type { BlockedDateRule } from "@/services/blockedDatesService";

// ─── Helpers de data (sem conversão UTC para evitar off-by-one) ───────────────

/** Cria um Date em hora local a partir de "YYYY-MM-DD" */
export const parseLocalDate = (str: string): Date => {
  const [year, month, day] = str.split("-").map(Number);
  return new Date(year, month - 1, day);
};

/** Formata um Date usando hora local → "YYYY-MM-DD" */
export const formatLocalDate = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// ─── DatePickerField ──────────────────────────────────────────────────────────

export interface DatePickerFieldProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  minDate?: string;
  restrictionType?: "rcc" | "vidro" | "rede_dor" | null;
  allowSunday?: boolean;
  allowAllDays?: boolean;
  blockedRules?: BlockedDateRule[];
  materialId?: string | number;
  packagingId?: string | number;
}

/**
 * Popover + Calendar usado tanto na criação da solicitação (DateStep) quanto
 * na edição (EditarSolicitacaoAgendamentoFields) — extraído para componente
 * próprio em vez de exportado direto de dentro de um step de wizard.
 */
export const DatePickerField: React.FC<DatePickerFieldProps> = ({
  value,
  onChange,
  minDate,
  restrictionType,
  allowSunday = false,
  allowAllDays = false,
  blockedRules,
  materialId,
  packagingId,
}) => {
  const [open, setOpen] = useState(false);

  const blockedMatcher = (date: Date) =>
    (blockedRules?.length ?? 0) > 0 &&
    isDateBlocked(
      date,
      materialId != null ? String(materialId) : null,
      packagingId != null ? Number(packagingId) : null,
      blockedRules ?? [],
    );

  const disabledMatcher = allowAllDays
    ? [{ before: parseLocalDate(formatLocalDate(new Date())) }, blockedMatcher]
    : [
        ...(!allowSunday ? [{ dayOfWeek: [0] as (0 | 1 | 2 | 3 | 4 | 5 | 6)[] }] : []),
        ...(minDate ? [{ before: parseLocalDate(minDate) }] : []),
        // Vidro + Bombona/Tambor: apenas terça (2) e sexta (5) — desabilita todos os outros (incl. domingo)
        ...(restrictionType === "vidro" ? [{ dayOfWeek: [0, 1, 3, 4, 6] as (0 | 1 | 2 | 3 | 4 | 5 | 6)[] }] : []),
        blockedMatcher,
      ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="mt-1 flex h-9 w-full items-center justify-between border-input px-3 text-left font-normal shadow-none"
        >
          <Text
            as="span"
            variant="body"
            size="small"
            className={value ? "text-neutral-700" : "text-neutral-500"}
          >
            {value ? format(value, "dd/MM/yyyy", { locale: ptBR }) : "Selecione"}
          </Text>
          <CalendarIcon className="h-4 w-4 text-neutral-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value ?? undefined}
          onSelect={(date) => {
            onChange(date ?? null);
            setOpen(false);
          }}
          disabled={disabledMatcher}
          locale={ptBR}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};
